import React, { useMemo } from 'react';
import { downloadJSON } from '../utils/download';
import { useI18n } from '../contexts/I18nContext';
import { usesOldNewCodes } from '../utils/color';
import PaletteGrid from './PaletteGrid';

export default function FinalPalette({ 
  pickedColors = [], 
  brand,
  ohuhuMode,
  onGenerateCode,
  onShare
}) {
  const { t } = useI18n();

  const palette = useMemo(() => {
    const uniquePalette = Array.from(new Set(pickedColors.map(pick => pick.recommendedMarker.hex)))
      .map(hex => pickedColors.find(pick => pick.recommendedMarker.hex === hex));

    return uniquePalette.map(pick => ({
      ...pick.recommendedMarker,
      hex: pick.recommendedMarker.hex,
      name: pick.recommendedMarker.name,
      brand: pick.recommendedMarker.brand,
      code: pick.recommendedMarker.code,
      code_old: pick.recommendedMarker.code_old,
      code_new: pick.recommendedMarker.code_new
    }));
  }, [pickedColors]);

  if (palette.length === 0) return null;

  const generatePaletteCode = () => {
    const timestamp = Date.now();
    const colorHashes = palette.map(marker => marker.hex.substring(1, 4)).join('');
    const paletteId = `CP${timestamp.toString(36).toUpperCase()}${colorHashes.toUpperCase()}`;
    
    const paletteData = {
      id: paletteId,
      type: 'color-picker',
      brand,
      colors: palette.map((marker, index) => ({
        hex: marker.hex,
        name: marker.name,
        brand: marker.brand,
        code: usesOldNewCodes(marker.brand)
          ? { old: marker.code_old, new: marker.code_new }
          : marker.code,
        originalColor: pickedColors[index].extractedColor.hex,
        position: pickedColors[index].position
      })),
      metadata: {
        createdAt: new Date().toISOString(),
        totalColors: palette.length
      }
    };

    onGenerateCode?.(paletteId, paletteData);
    return paletteId;
  };

  const handleDownload = () => {
    const exportData = {
      metadata: {
        generatedAt: new Date().toISOString(),
        totalColors: palette.length,
        mode: 'color-picker-palette',
        selectedBrand: brand
      },
      palette: palette.map((marker, index) => ({
        position: index + 1,
        hex: marker.hex,
        name: marker.name,
        brand: marker.brand,
        code: marker.code,
        ...(usesOldNewCodes(marker.brand) && {
          code_old: marker.code_old,
          code_new: marker.code_new
        }),
        extracted_color: pickedColors[index].extractedColor,
        canvas_position: pickedColors[index].position
      }))
    };

    downloadJSON(exportData, `color-picker-palette-${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-display font-semibold text-slate-700 mb-2">
          {t('finalPalette.title')}
        </h3>
        <span>
          {t('finalPalette.subtitle', { count: palette.length })}
        </span>
        <p className="text-sm text-elegant mb-4">
          {t('finalPalette.ready')}
        </p>
        <div className="w-24 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full mx-auto"></div>
      </div>

      {/* Palette Grid */}
      <PaletteGrid
        palette={palette}
        locks={[]}
        onToggleLock={() => {}}
        ohuhuMode={ohuhuMode}
        showLockIcons={false}
        compactMode={true}
      />
    </div>
  );
}
